import React, { useState, useEffect } from 'react';
import { Treatment, PracticeLocation, SiteSettings } from '../types';
import { apiService } from '../services/apiService';

interface AdminDashboardProps {
  treatments: Treatment[];
  locations: PracticeLocation[];
  settings: SiteSettings;
  categories: string[];
  onUpdate: () => void;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ 
  treatments, 
  locations, 
  settings, 
  categories, 
  onUpdate 
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'settings' | 'treatments' | 'categories' | 'locations'>('settings');
  
  // Local states
  const [siteSettings, setSiteSettings] = useState<SiteSettings>(settings);
  const [treatmentsList, setTreatmentsList] = useState<Treatment[]>(treatments);
  const [locationsList, setLocationsList] = useState<PracticeLocation[]>(locations);
  const [categoriesList, setCategoriesList] = useState<string[]>(categories);
  
  // Editor states
  const [editingTreatment, setEditingTreatment] = useState<Treatment | null>(null);
  const [editingLocation, setEditingLocation] = useState<PracticeLocation | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');

  useEffect(() => {
    setSiteSettings(settings);
    setTreatmentsList(treatments);
    setLocationsList(locations);
    setCategoriesList(categories);
  }, [settings, treatments, locations, categories]);

  const handleLogout = async () => {
    await apiService.logout();
    window.location.reload();
  };

  const syncAll = async (
    newTreatments?: Treatment[], 
    newLocations?: PracticeLocation[], 
    newSettings?: SiteSettings,
    newCats?: string[]
  ) => {
    setIsSaving(true);
    try {
      const dataToSync = {
        treatments: newTreatments || treatmentsList,
        locations: newLocations || locationsList,
        settings: newSettings || siteSettings,
        categories: newCats || categoriesList
      };
      await apiService.syncToCloud(dataToSync);
      onUpdate();
      alert("Wijzigingen succesvol opgeslagen.");
    } catch (e: any) {
      alert("Fout bij opslaan: " + (e.message || "Onbekende fout"));
    } finally {
      setIsSaving(false);
    }
  };

  // Treatment Array Helpers
  const addBenefit = () => {
    if (!editingTreatment) return;
    const benefit = prompt("Voeg een voordeel toe:");
    if (benefit) {
      setEditingTreatment({
        ...editingTreatment,
        benefits: [...(editingTreatment.benefits || []), benefit]
      });
    }
  };

  const addProcessStep = () => {
    if (!editingTreatment) return;
    const title = prompt("Titel van de stap:");
    const description = prompt("Beschrijving van de stap:");
    if (title) {
      const stepNumber = (editingTreatment.process?.length || 0) + 1;
      setEditingTreatment({
        ...editingTreatment,
        process: [...(editingTreatment.process || []), { step: stepNumber, title, description: description || '' }]
      });
    }
  };

  const addFaq = () => {
    if (!editingTreatment) return;
    const question = prompt("Vraag:");
    const answer = prompt("Antwoord:");
    if (question && answer) {
      setEditingTreatment({
        ...editingTreatment,
        faqs: [...(editingTreatment.faqs || []), { question, answer }]
      });
    }
  };

  const saveTreatment = () => {
    if (!editingTreatment) return;
    let newList;
    if (treatmentsList.find(t => t.id === editingTreatment.id)) {
      newList = treatmentsList.map(t => t.id === editingTreatment.id ? editingTreatment : t);
    } else {
      newList = [...treatmentsList, editingTreatment];
    }
    setTreatmentsList(newList);
    setEditingTreatment(null);
    syncAll(newList);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, field: 'imageUrl' | 'detailImageUrl') => {
    if (!e.target.files?.[0] || !editingTreatment) return;
    try {
      const url = await apiService.uploadImage(e.target.files[0]);
      setEditingTreatment({ ...editingTreatment, [field]: url });
    } catch (err) {
      alert("Upload mislukt.");
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12">
          <div>
            <h1 className="font-display text-3xl tracking-widest uppercase">MD Dashboard</h1>
            <p className="text-primary text-[10px] font-bold uppercase tracking-[0.3em]">Beheeromgeving Dr. An De Cuyper</p>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={handleLogout} className="bg-white/5 hover:bg-white/10 px-6 py-3 rounded-xl text-[10px] font-bold uppercase tracking-widest border border-white/10">Uitloggen</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 md:gap-8 mb-12 border-b border-white/10 overflow-x-auto no-scrollbar">
          {(['settings', 'treatments', 'categories', 'locations'] as const).map(tab => (
            <button 
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-4 text-[10px] font-bold uppercase tracking-widest whitespace-nowrap transition-all ${activeTab === tab ? 'text-primary border-b border-primary' : 'text-white/40 hover:text-white'}`}
            >
              {tab === 'settings' ? 'Site & Branding' : 
               tab === 'treatments' ? 'Behandelingen' : 
               tab === 'categories' ? 'Categorieën' : 'Locaties'}
            </button>
          ))}
        </div>

        {/* Tab Content: Settings */}
        {activeTab === 'settings' && (
          <div className="bg-white/5 border border-white/10 p-8 md:p-12 rounded-[2.5rem] space-y-12 animate-fadeIn">
            
            {/* Branding */}
            <div className="space-y-6">
              <h3 className="text-primary text-xs font-bold uppercase tracking-widest border-b border-white/10 pb-4">Algemeen & Branding</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div className="space-y-3">
                  <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest">Kliniek Naam</label>
                  <input type="text" value={siteSettings.clinicName} onChange={(e) => setSiteSettings({...siteSettings, clinicName: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 outline-none focus:border-primary" />
                </div>
                 <div className="space-y-3">
                  <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest">Logo URL</label>
                  <input type="text" value={siteSettings.logoUrl} onChange={(e) => setSiteSettings({...siteSettings, logoUrl: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 outline-none focus:border-primary" />
                </div>
                <div className="space-y-3">
                  <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest">Navigatiebalk Kleur</label>
                  <div className="flex gap-4">
                    <input type="color" value={siteSettings.navbarColor || '#0a0a0a'} onChange={(e) => setSiteSettings({...siteSettings, navbarColor: e.target.value})} className="w-16 h-14 bg-white/5 border border-white/10 rounded-2xl p-1 cursor-pointer" />
                    <input type="text" value={siteSettings.navbarColor} onChange={(e) => setSiteSettings({...siteSettings, navbarColor: e.target.value})} className="flex-grow bg-white/5 border border-white/10 rounded-2xl px-6 py-4 font-mono" />
                  </div>
                </div>
                <div className="space-y-3">
                  <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest">Hero Titel</label>
                  <input type="text" value={siteSettings.heroTitle} onChange={(e) => setSiteSettings({...siteSettings, heroTitle: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 outline-none focus:border-primary" />
                </div>
                <div className="space-y-3 md:col-span-2">
                  <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest">Hero Ondertitel</label>
                  <textarea value={siteSettings.heroSubtitle} onChange={(e) => setSiteSettings({...siteSettings, heroSubtitle: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 outline-none focus:border-primary h-24" />
                </div>
                 <div className="space-y-3 md:col-span-2">
                  <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest">Footer Tekst</label>
                  <input type="text" value={siteSettings.footerText} onChange={(e) => setSiteSettings({...siteSettings, footerText: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 outline-none focus:border-primary" />
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="space-y-6">
              <h3 className="text-primary text-xs font-bold uppercase tracking-widest border-b border-white/10 pb-4">Contact & Socials</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div className="space-y-3">
                  <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest">E-mailadres</label>
                  <input type="email" value={siteSettings.email} onChange={(e) => setSiteSettings({...siteSettings, email: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 outline-none focus:border-primary" />
                </div>
                <div className="space-y-3">
                  <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest">Telefoonnummer</label>
                  <input type="text" value={siteSettings.phone} onChange={(e) => setSiteSettings({...siteSettings, phone: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 outline-none focus:border-primary" />
                </div>
                <div className="space-y-3">
                  <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest">Instagram URL</label>
                  <input type="text" value={siteSettings.instagram} onChange={(e) => setSiteSettings({...siteSettings, instagram: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 outline-none focus:border-primary" placeholder="https://instagram.com/..." />
                </div>
                <div className="space-y-3">
                  <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest">Facebook URL</label>
                  <input type="text" value={siteSettings.facebook} onChange={(e) => setSiteSettings({...siteSettings, facebook: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 outline-none focus:border-primary" placeholder="https://facebook.com/..." />
                </div>
                <div className="space-y-3 md:col-span-2">
                  <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest">Afspraak URL (Rosa/Online Agenda)</label>
                  <input type="text" value={siteSettings.bookingUrl} onChange={(e) => setSiteSettings({...siteSettings, bookingUrl: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 outline-none focus:border-primary" />
                </div>
              </div>
            </div>

            <button onClick={() => syncAll(undefined, undefined, siteSettings)} disabled={isSaving} className="bg-primary text-black px-12 py-4 rounded-xl font-black uppercase tracking-widest text-[10px] hover:bg-white transition-all">Instellingen Opslaan</button>
          </div>
        )}

        {/* Tab Content: Categories */}
        {activeTab === 'categories' && (
          <div className="bg-white/5 border border-white/10 p-8 md:p-12 rounded-[2.5rem] space-y-8 animate-fadeIn">
            <div className="flex gap-4 max-w-md">
              <input type="text" value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} placeholder="Nieuwe categorie..." className="flex-grow bg-white/5 border border-white/10 rounded-2xl px-6 py-4 outline-none focus:border-primary" />
              <button onClick={() => {
                if(!newCategoryName) return;
                const newList = [...categoriesList, newCategoryName];
                setCategoriesList(newList);
                setNewCategoryName('');
                syncAll(undefined, undefined, undefined, newList);
              }} className="bg-primary text-black px-8 py-4 rounded-2xl font-black uppercase text-[10px]">Toevoegen</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {categoriesList.map(cat => (
                <div key={cat} className="bg-white/5 border border-white/10 p-4 rounded-xl flex justify-between items-center">
                  <span className="font-bold">{cat}</span>
                  <button onClick={() => {
                    const newList = categoriesList.filter(c => c !== cat);
                    setCategoriesList(newList);
                    syncAll(undefined, undefined, undefined, newList);
                  }} className="text-red-500 hover:text-white transition-colors"><span className="material-icons text-sm">delete</span></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab Content: Treatments */}
        {activeTab === 'treatments' && (
          <div className="space-y-8 animate-fadeIn">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-xl font-display uppercase tracking-widest">Behandelingen</h2>
              <button onClick={() => setEditingTreatment({
                id: `t-${Date.now()}`, name: '', category: categoriesList[0] || '', description: '', price: '', duration: '15 min', recovery: 'Geen', visibility: '3-4 mnd', details: '', benefits: [], process: [], faqs: [], imageUrl: '', detailImageUrl: ''
              })} className="bg-primary text-black px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-white transition-all">+ Nieuw</button>
            </div>
            <div className="grid grid-cols-1 gap-4">
              {treatmentsList.map(t => (
                <div key={t.id} className="bg-white/5 border border-white/10 p-6 rounded-2xl flex items-center justify-between group hover:border-primary/30 transition-all">
                  <div className="flex items-center gap-6">
                    <img src={t.imageUrl} className="w-12 h-12 rounded-lg object-cover grayscale" />
                    <div>
                      <h3 className="font-bold">{t.name}</h3>
                      <p className="text-white/40 text-[10px] uppercase tracking-widest">{t.category} • €{t.price}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEditingTreatment(t)} className="p-3 bg-white/5 hover:bg-primary hover:text-black rounded-xl transition-all"><span className="material-icons text-sm">edit</span></button>
                    <button onClick={() => {
                      const newList = treatmentsList.filter(item => item.id !== t.id);
                      setTreatmentsList(newList);
                      syncAll(newList);
                    }} className="p-3 bg-white/5 hover:bg-red-500 rounded-xl transition-all"><span className="material-icons text-sm">delete</span></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Treatment Editor Modal */}
        {editingTreatment && (
          <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-[#111] border border-white/10 w-full max-w-4xl my-8 rounded-[3rem] overflow-hidden shadow-2xl flex flex-col max-h-[90vh]">
              <div className="p-8 border-b border-white/10 flex justify-between items-center bg-white/[0.02]">
                <h3 className="font-display text-xl uppercase tracking-widest">Bewerken</h3>
                <button onClick={() => setEditingTreatment(null)} className="text-white/40 hover:text-white"><span className="material-icons">close</span></button>
              </div>
              
              <div className="p-8 md:p-12 overflow-y-auto space-y-12">
                {/* Basis Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-white/20 uppercase tracking-widest">Naam</label>
                    <input type="text" value={editingTreatment.name} onChange={e => setEditingTreatment({...editingTreatment, name: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 outline-none focus:border-primary" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-white/20 uppercase tracking-widest">Categorie</label>
                    <select value={editingTreatment.category} onChange={e => setEditingTreatment({...editingTreatment, category: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 outline-none focus:border-primary">
                      {categoriesList.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-white/20 uppercase tracking-widest">Prijs (Tekst of Getal)</label>
                    <input type="text" value={editingTreatment.price} onChange={e => setEditingTreatment({...editingTreatment, price: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 outline-none focus:border-primary" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-white/20 uppercase tracking-widest">Duur</label>
                    <input type="text" value={editingTreatment.duration} onChange={e => setEditingTreatment({...editingTreatment, duration: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 outline-none focus:border-primary" />
                  </div>
                   <div className="space-y-2">
                    <label className="text-[10px] font-black text-white/20 uppercase tracking-widest">Herstel</label>
                    <input type="text" value={editingTreatment.recovery} onChange={e => setEditingTreatment({...editingTreatment, recovery: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 outline-none focus:border-primary" />
                  </div>
                   <div className="space-y-2">
                    <label className="text-[10px] font-black text-white/20 uppercase tracking-widest">Zichtbaarheid</label>
                    <input type="text" value={editingTreatment.visibility} onChange={e => setEditingTreatment({...editingTreatment, visibility: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 outline-none focus:border-primary" />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-white/20 uppercase tracking-widest">Beschrijving (Kort)</label>
                  <textarea value={editingTreatment.description} onChange={e => setEditingTreatment({...editingTreatment, description: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 outline-none focus:border-primary h-24" />
                </div>
                
                 <div className="space-y-2">
                  <label className="text-[10px] font-black text-white/20 uppercase tracking-widest">Details (Lang)</label>
                  <textarea value={editingTreatment.details} onChange={e => setEditingTreatment({...editingTreatment, details: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 outline-none focus:border-primary h-40" />
                </div>

                {/* Benefits Section */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b border-white/5 pb-2">
                    <label className="text-[10px] font-black text-white/20 uppercase tracking-widest">Voordelen (Benefits)</label>
                    <button onClick={addBenefit} className="text-primary text-[10px] font-bold uppercase">+ Voeg toe</button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {editingTreatment.benefits?.map((b, i) => (
                      <div key={i} className="bg-white/5 p-3 rounded-xl flex justify-between items-center text-xs">
                        <span>{b}</span>
                        <button onClick={() => setEditingTreatment({...editingTreatment, benefits: editingTreatment.benefits.filter((_, idx) => idx !== i)})} className="text-red-500"><span className="material-icons text-xs">close</span></button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Process Section */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b border-white/5 pb-2">
                    <label className="text-[10px] font-black text-white/20 uppercase tracking-widest">Behandeltraject (Stappen)</label>
                    <button onClick={addProcessStep} className="text-primary text-[10px] font-bold uppercase">+ Voeg stap toe</button>
                  </div>
                  <div className="space-y-3">
                    {editingTreatment.process?.map((p, i) => (
                      <div key={i} className="bg-white/5 p-4 rounded-xl flex justify-between items-start gap-4">
                        <div className="flex-grow">
                          <p className="font-bold text-xs uppercase text-primary mb-1">Stap {p.step}: {p.title}</p>
                          <p className="text-white/40 text-[11px]">{p.description}</p>
                        </div>
                        <button onClick={() => setEditingTreatment({...editingTreatment, process: editingTreatment.process.filter((_, idx) => idx !== i)})} className="text-red-500"><span className="material-icons text-xs">delete</span></button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* FAQ Section */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b border-white/5 pb-2">
                    <label className="text-[10px] font-black text-white/20 uppercase tracking-widest">Veelgestelde Vragen (FAQ)</label>
                    <button onClick={addFaq} className="text-primary text-[10px] font-bold uppercase">+ Voeg vraag toe</button>
                  </div>
                  <div className="space-y-3">
                    {editingTreatment.faqs?.map((f, i) => (
                      <div key={i} className="bg-white/5 p-4 rounded-xl space-y-2">
                        <div className="flex justify-between items-center">
                          <p className="font-bold text-xs">{f.question}</p>
                          <button onClick={() => setEditingTreatment({...editingTreatment, faqs: editingTreatment.faqs.filter((_, idx) => idx !== i)})} className="text-red-500"><span className="material-icons text-xs">close</span></button>
                        </div>
                        <p className="text-white/40 text-[11px] italic">{f.answer}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Images */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <label className="text-[10px] font-black text-white/20 uppercase tracking-widest">Hoofdafbeelding</label>
                    {editingTreatment.imageUrl && <img src={editingTreatment.imageUrl} className="w-full h-32 object-cover rounded-xl mb-2 grayscale" />}
                    <input type="file" onChange={e => handleImageUpload(e, 'imageUrl')} className="text-xs" />
                  </div>
                  <div className="space-y-4">
                    <label className="text-[10px] font-black text-white/20 uppercase tracking-widest">Detailachtergrond</label>
                    {editingTreatment.detailImageUrl && <img src={editingTreatment.detailImageUrl} className="w-full h-32 object-cover rounded-xl mb-2 grayscale" />}
                    <input type="file" onChange={e => handleImageUpload(e, 'detailImageUrl')} className="text-xs" />
                  </div>
                </div>
              </div>

              <div className="p-8 border-t border-white/10 bg-white/[0.02] flex justify-end gap-4">
                <button onClick={() => setEditingTreatment(null)} className="px-8 py-4 text-[10px] font-black uppercase text-white/40">Annuleren</button>
                <button onClick={saveTreatment} className="bg-primary text-black px-12 py-4 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-white transition-all">Behandeling Opslaan</button>
              </div>
            </div>
          </div>
        )}

        {/* Tab Content: Locations */}
        {activeTab === 'locations' && (
          <div className="space-y-8 animate-fadeIn">
             <div className="flex justify-between items-center mb-8">
              <h2 className="text-xl font-display uppercase tracking-widest">Locaties</h2>
              <button onClick={() => setEditingLocation({ id: `${Date.now()}`, name: '', address: '', mapsUrl: '' })} className="bg-primary text-black px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest">+ Nieuw</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {locationsList.map(l => (
                <div key={l.id} className="bg-white/5 border border-white/10 p-8 rounded-3xl space-y-4">
                  <h3 className="font-display text-xl">{l.name}</h3>
                  <p className="text-white/40 text-sm">{l.address}</p>
                  <div className="flex gap-3 pt-4 border-t border-white/5">
                    <button onClick={() => setEditingLocation(l)} className="flex-grow bg-white/5 py-3 rounded-xl text-[10px] font-bold uppercase">Bewerken</button>
                    <button onClick={() => {
                      const newList = locationsList.filter(loc => loc.id !== l.id);
                      setLocationsList(newList);
                      syncAll(undefined, newList);
                    }} className="px-4 bg-white/5 hover:bg-red-500 rounded-xl"><span className="material-icons text-sm">delete</span></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Location Modal */}
        {editingLocation && (
          <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4">
            <div className="bg-[#111] border border-white/10 w-full max-w-md rounded-[2.5rem] p-8 space-y-6">
              <h3 className="font-display text-xl uppercase tracking-widest">Locatie</h3>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-white/20 uppercase tracking-widest">Naam</label>
                <input type="text" value={editingLocation.name} onChange={e => setEditingLocation({...editingLocation, name: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-white/20 uppercase tracking-widest">Adres</label>
                <textarea value={editingLocation.address} onChange={e => setEditingLocation({...editingLocation, address: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 h-24" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-white/20 uppercase tracking-widest">Google Maps Link</label>
                <input type="text" value={editingLocation.mapsUrl || ''} onChange={e => setEditingLocation({...editingLocation, mapsUrl: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4" placeholder="https://maps.google.com/..." />
              </div>
              <div className="flex justify-end gap-4 pt-4">
                <button onClick={() => setEditingLocation(null)} className="text-[10px] font-bold uppercase text-white/40">Annuleren</button>
                <button onClick={() => {
                  let newList;
                  if (locationsList.find(l => l.id === editingLocation.id)) {
                    newList = locationsList.map(l => l.id === editingLocation.id ? editingLocation : l);
                  } else {
                    newList = [...locationsList, editingLocation];
                  }
                  setLocationsList(newList);
                  setEditingLocation(null);
                  syncAll(undefined, newList);
                }} className="bg-primary text-black px-8 py-3 rounded-xl text-[10px] font-black uppercase">Opslaan</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;